import { useEffect, useRef, useState } from 'preact/hooks';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { BtnPrimary } from '@components/buttons';
import { showModalForm } from '@components/modal-form';
import { useIntl } from 'shared/intl/use-intl';
import { ImageEditor } from './image-editor';
import { ImagePreview } from './image-preview';
import { isEditableImage } from './sources';

export type EditImageOpts = {
  aspectRatio?: number;
  requireCrop?: boolean;
};

function ReviewImageDialog({
  file,
  opts,
  resolve,
}: {
  file: File;
  opts: EditImageOpts;
  resolve(file?: File): void;
}) {
  const intl = useIntl();
  const [previewUrl] = useState(() => URL.createObjectURL(file));
  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  return (
    <ImageEditor
      file={file}
      previewUrl={previewUrl}
      aspectRatio={opts.aspectRatio}
      requireCrop={opts.requireCrop}
      confirmText={intl('Upload')}
      onCancel={() => resolve(undefined)}
      onConfirm={(edited) => resolve(edited || (opts.requireCrop ? undefined : file))}
    />
  );
}

type ReviewItem = { id: number; file: File; previewUrl: string };

function ReviewImagesDialog({ files, resolve }: { files: File[]; resolve(files?: File[]): void }) {
  const intl = useIntl();
  const [items, setItems] = useState<ReviewItem[]>(() =>
    files.map((file, id) => ({ id, file, previewUrl: URL.createObjectURL(file) })),
  );
  const [editingId, setEditingId] = useState<number>();
  const editing = items.find((item) => item.id === editingId);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(
    () => () => {
      itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    },
    [],
  );

  if (editing) {
    return (
      <ImageEditor
        file={editing.file}
        previewUrl={editing.previewUrl}
        onCancel={() => setEditingId(undefined)}
        onConfirm={(edited) => {
          if (edited && edited !== editing.file) {
            URL.revokeObjectURL(editing.previewUrl);
            setItems((current) =>
              current.map((item) =>
                item.id === editing.id
                  ? { ...item, file: edited, previewUrl: URL.createObjectURL(edited) }
                  : item,
              ),
            );
          }
          setEditingId(undefined);
        }}
      />
    );
  }

  return (
    <BaseDialog onClose={() => resolve(undefined)} blurBg={false} zIndex="z-[70]">
      <div class="w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden rounded-lg bg-white">
        <header class="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 class="font-semibold text-lg">{intl('Review selected images')}</h2>
          <p class="text-gray-600 text-sm mt-1">
            {intl('Edit an image before uploading if needed.')}
          </p>
        </header>
        <div class="flex-grow p-6 min-h-0 overflow-y-auto">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((item) => (
              <ImagePreview
                key={item.id}
                file={item.file}
                previewUrl={item.previewUrl}
                status="pending"
                progress={0}
                onEdit={() => setEditingId(item.id)}
                onRetry={() => undefined}
                onSkip={() => undefined}
                disabled={false}
              />
            ))}
          </div>
        </div>
        <footer class="p-6 pt-4 border-t border-gray-200 flex-shrink-0 flex items-center gap-3">
          <BtnPrimary class="rounded-md" onClick={() => resolve(items.map((item) => item.file))}>
            {intl('Upload all images')} ({items.length})
          </BtnPrimary>
          <BtnCancel
            class="px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            onClick={() => resolve(undefined)}
          >
            {intl('Cancel')}
          </BtnCancel>
        </footer>
      </div>
    </BaseDialog>
  );
}

export function editImage(file: File, opts: EditImageOpts = {}) {
  return showModalForm<File>(({ resolve }) => (
    <ReviewImageDialog file={file} opts={opts} resolve={resolve} />
  ));
}

export async function reviewImages(files: File[]): Promise<File[]> {
  if (!files.length || !files.every(isEditableImage)) {
    return files;
  }
  if (files.length === 1) {
    const edited = await editImage(files[0]);
    return edited ? [edited] : [];
  }
  const reviewed = await showModalForm<File[]>(({ resolve }) => (
    <ReviewImagesDialog files={files} resolve={resolve} />
  ));
  return reviewed || [];
}
